import React from 'react';
import { Compass, Mail, ChevronRight, ArrowRight, ArrowLeft, Calendar, User, Volume2 } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { ArchiveTab } from '../../types';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface SectionProps {
  onNavigate?: (tab: ArchiveTab) => void;
}

export const SuffolkToSouthernSeasSection: React.FC<SectionProps> = ({ onNavigate }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();
  return (
    <div className="space-y-10">
      
      {/* Header & Context */}
      <header className="bg-[#f3ede0] p-8 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        {/* Clean Breadcrumb & Voice Action */}
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          <div className="flex flex-wrap items-center gap-2">
            {onNavigate && (
              <button
                onClick={() => onNavigate('journey')}
                className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
              >
                <Compass className="w-3.5 h-3.5" />
                <span>The Journey</span>
              </button>
            )}
            <ChevronRight className="w-3 h-3 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a]">
              The Passage to Canterbury
            </span>
          </div>

          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs"
            title="Listen to From Suffolk to the Southern Seas"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Page...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight">
          From Suffolk to the Southern Seas
        </h1>

        <p className="font-serif text-base md:text-xl text-[#7a5b3a] italic leading-relaxed border-l-4 border-[#7a5b3a] pl-4 my-2">
          The 1850 voyage of John and Anne Denton and their seven children from Suffolk to New Zealand aboard small sailing ships. (Based on fact and imagination, this next section explores what might have been.)
        </p>
      </header>

      {/* Main Archival Document */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-14 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-[#2c221e]">
        
        {/* Verbatim Section 1 */}
        <div className="space-y-6 text-base md:text-lg leading-relaxed">
          <p>
            In 1850 John and Anne Denton and their seven children left their Suffolk security in anticipation of an idealised slice of England in New Zealand - instead, they found wretchedness, hard work and death.
          </p>

          <p>
            Anne Denton Pryke (1812 – 51) was a reluctant colonist. She lived a settled rural life in comfortable circumstances, happy in her household with family and friends all around, until 1850 when the family went to New Zealand, sailing for 99 days in a small ship to arrive on 17 December. Eight months later, Anne was dead from the fever that had already taken the lives of three of her children.
          </p>

          <p>
            Anne Maria Pryke was born 1 October 1812 in the pretty and hilly Suffolk village of Kersey with its many thatched roofed cottages against a wooded background, best known for its thick woollen Kersey cloth.
          </p>
        </div>

        {/* Image 5: Kersey */}
        <ArchivalImageCard
          src="/images/5 Kersey.jpg"
          alt="Kersey"
          caption="Kersey"
          filenameHint="5 Kersey.jpg"
        />

        <div className="space-y-6 text-base md:text-lg leading-relaxed">
          <p>
            She was named for her mother, Anna Maria Bradstreet (1788 – 1879), who was proud of her maternal French heritage. It was perhaps from her than Anna’s two slender dark-haired daughters had inherited attractive delicate features, with gentle but observant brown eyes.
          </p>

          <p>
            Anne’s father, John Pryke (1787 – 1870), continued the family tradition of sheep and crop farming. Over generations the Pryke family had established a portfolio of commercial buildings in Bury St Edmunds, the nearest large town. That income was essential to support the family and their workers during the inevitable lean years of drought, unseasonable harvests and crop failures. Pryke sons experimented with crops, breeds, new machinery and took part in local affairs. Pryke daughters married established tenant farmers, yeomen and ministers.
          </p>

          <p>
            Anne married into the Denton family in 1831, gaining seven brothers and three sisters-in-law. John’s second daughter, Sophia, lived comfortably nearby at Great Barton with her farmer and trader husband, Bartrum Bardwell. She too took pride in her French heritage and named her children Dominic, Louis, Pierre, Juliette and Delphine.
          </p>

          <p>
            Tall and quiet John Pryke outlived his elder daughter by 19 years. He worshipped, grieved and took part in parish affairs at the stone and flint St Nicholas at Little Whelnetham, a typical village church with a stout short tower. Old yew trees protected the grounds from the coldest winds. Flat countryside met cloudy horizons; wooden posts lined laneways with puddles often glistening in whatever sun shone weakly or brightly. It was his custom after Sunday morning service, wet or fine, to walk between the headstone rows and remember and to put garden flowers on the tiny headstone of his granddaughter, Anne’s little Elizabeth, who died in 1845, aged three.
          </p>
        </div>

        {/* Image 6: St Nicholas, Suffolk */}
        <ArchivalImageCard
          src="/images/6St Nicholas, Suffolk.jpg"
          alt="St Nicholas, Suffolk"
          caption="St Nicholas, Suffolk – the registers date from 1568"
          filenameHint="6St Nicholas, Suffolk.jpg"
        />

        <div className="space-y-6 text-base md:text-lg leading-relaxed">
          <p>
            Sophia broke the sad news of Anne’s death to her parents. They accepted it with the same stoicism they greeted all adversity, but did wonder if Anne’s reluctance to leave had been because of some premonition. They carried on with quiet and characteristic dignity, keeping their grief private and sharing their heartbreak only with Sophia.
          </p>

          <p>
            John Pryke died a quiet death at the grand age of 83. Then he joined family and friends within the churchyard’s stoned walls, buried in his finest suit of Kersey cloth as he had instructed. A small procession walked up the hill, past the pink house at the bottom, up past the yellow, white and blue thatched cottages that opened directly on to the narrow road. His neighbors’ said he had lived a good life as they drank a toast to his memory.
          </p>

          <p>
            “It’s a bloody shame he never saw Anne again after she was dragged off to that New Zealand,” muttered Harry Constable one cold night in the local pub. Two old men exchanged glances as they recollected the time when young Harry had been sweet on the pretty Anne, but he never made the most of his chances and she married a man 23 years her senior. They remembered, because Harry got drunk that night. They took him home, telling him there would be another sweet girl to capture his heart, but they were wrong. From that day, Harry Constable never had a good word to say about the Denton family. When he heard John was taking the family away, he warned anyone who would listen that no good would come of it. When Anne said her quiet goodbye to Harry and his parents, she had no idea she was leaving behind a man who thought his heart had been broken.
          </p>

          <p>
            Anne’s mother lived to her 91st year, quietly and independently, gradually accepting more assistance as she needed it. Every Sunday she laid flowers on John’s and little Elizabeth’s graves until she quietly expired in her sleep, 28 years after her precious daughter died on the other side of the world.
          </p>
        </div>

        {/* Image 7: Great Barton Church */}
        <ArchivalImageCard
          src="/images/7 Great Barton Church.jpg"
          alt="Great Barton Church of Holy Innocents"
          caption="Great Barton Church of Holy Innocents (rebuilt 15thC) where many Dentons are buried. There are Dog Pews at the back of the church, for shepherds working in this wool producing area"
          filenameHint="7 Great Barton Church.jpg"
        />

        <div className="space-y-6 text-base md:text-lg leading-relaxed">
          <p>
            The Suffolk Dentons farmed in peace and relative prosperity, confident in their place in society. The names written in the family Bibles dated back to the 1600s: John, Frederick, Edward, William, Charles, Henry, Thomas, Anne Elizabeth, Sophie Sarah, Susan, Georgiana, Mary and Miriam. Their headstones were mossy with age. Ivy grew over grave curbs. There were a few obelisks, and some were interred within a sarcophagus. Oaks and elms grew tall and spread wide and dropped leaves on the path inside the lynch gate. Harry Constable regularly and ruthlessly trimmed the dense yew hedges around the churchyard.
          </p>

          <p>
            West Suffolk’s largest town, Bury St Edmunds, about 80 miles northeast of London, could not remain remote from sweeping changes and social chaos as poverty became widespread and many faced the dreaded workhouse. The impact of new industrial age machines meant many tradespeople and skilled workers lost their incomes. The parish mood reflected the mood of the continent. Reverend Charles Torlesse of Stoke-by-Nayland invited his brother-in-law, Edward Gibbon Wakefield, to come and discuss emigration and the idea of creating a utopian society in new lands. Newspapers carried editorials arguing emigration was better than the workhouse. The Irish potato famine heaped more despair on the struggling poor and masses were seeking hope in England. These issues were now being seriously discussed by people who usually gave their attention to purely domestic affairs. As devout Anglicans, the Dentons were deeply concerned, and John listened to this daring philosophical talk. He looked at opportunities for his five sons – Arthur, William, Frederick, Edward and Thomas. Anne thought the discussions were only of academic interest as John, aged 50, was too old to emigrate.
          </p>

          <p className="italic text-[#7a5b3a] bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9]">
            (The following letters have been made up but are based on fact and actual events at the time. They are included to illustrate the personal realities of emigration at that time.)
          </p>

          <p className="font-serif font-bold text-[#4a3525] text-lg">
            Colonel Robert Rushbrooke was the Member of Parliament for West Suffolk. John Denton wrote to him in early 1845.
          </p>
        </div>

        {/* Letter 1: John Denton to Col Robert Rushbrooke (1845) */}
        <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
          <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
            <span>Letter to Col. Robert Rushbrooke</span>
            <span>Early 1845</span>
          </div>
          
          <div className="space-y-4 text-base md:text-lg leading-relaxed">
            <p>
              "There have been more than 150 local arson attacks so far this year. I bring this to your attention with a sense of urgency amidst general unrest. Many are still very much against the railway, but I sense most are gradually coming to accept there is no turning back. Your agent will have told you the stagecoach and barge men are in fear for their livelihoods. The government must think about how their losses can be mitigated – I leave that to you.
            </p>

            <p>
              You asked about progress for the celebrations to mark the opening of the Bury rail next year. The parish discussed it at some length. There was most support for fireworks (I think any excuse for some), street parties and a civic banquet. Anne is very happy to take on the preparations for the banquet. We are pleased you will be our Master of Ceremonies. The ladies are keen and already talking about what they will wear. I have taken the liberty of assuring them there will be photographers in attendance!
            </p>

            <p>
              We hear dreadful reports about the Irish potato famine. Many poor Irish are here begging for food. There is constant talk of emigration, even Parish meetings spend more time on such topics - the state of the roof is no longer the prime concern.
            </p>

            <p>
              I don’t support those who have damaged new machines, but when a man loses his livelihood and has no other way to support his family he may well resort to criminal activity in his despair. I hope you can persuade your fellow lawmakers to take a broad view on these matters. I’ve heard there are more than 200 people in the workhouse – even bricklayers and carpenters, all decent men who plied an honest trade. There have been isolated cases of fever and rabies. I urge you to look at reforms that will improve life for the ordinary Englishman. They surely deserve good government as well as the mercy of Our Lord."
            </p>
          </div>
        </div>

        {/* Letter 2: Anne Denton to Sophia (September 1846) */}
        <div className="space-y-4">
          <p className="font-serif font-bold text-[#4a3525] text-lg">
            From Anne Denton’s letter to her sister, September 1846
          </p>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>September 1846</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dear Sophia,<br />
                Mark your diary for early December - we are having a civic banquet and ball for the opening of the railway and Bury St Edmunds will be joining in the street parties and events for this great event. John is concerned about the impacts it is already having, but I am happy to be involved in the celebrations. I need you to be here too, in your very best finery no less. Reasons to celebrate are few and far between so I want this to be something to remember.
              </p>

              <p>
                Bring all the children – I hope our children will forever remember the day the railway came to Bury. It’s so nice to have something happy after all the troubles in Suffolk, although John tells me it is far worse in other places. Everywhere there is talk about the debts people are accumulating. Too many good honest tradespeople have lost their livelihoods because of new machinery in factories.
              </p>

              <p>
                Col. Rushbrooke and John have been talking about emigration. John said he cannot see any prospect of our boys getting their own farms.
              </p>

              <p>
                I will get my dusky pink silk ballgown altered. A lot of women are re-using their materials. A Belgium woman in Bury makes beautiful dresses and I trust her to make something rather wonderful, even if I have to have my baby belly strapped in!
              </p>

              <p>
                It is only six months since we buried our sweet little Lizzie. I have sad moments every day but her spirit will stay with me forever. Georgiana is so loving and often sits with me and tells me she misses her sister so we share a memory. Baby Alice is such a comfort and having her to look after has helped me heal. She looks so much like Lizzie.
              </p>

              <p>
                I hope you can come mid-November. I think you will soon love my friend Catherine. Her husband Charles is the vicar at Stoke-by-Nayland, a perfect gentleman, but he encourages John about the Canterbury Association. I wish he would not. Now Arthur is talking and I pretend to be deaf – I never want to leave Suffolk. Bring all the new accessories from London you’ve got. I hope this finds you and Bartrum and the little ones in good health.<br />
                Love from Anne"
              </p>
            </div>
          </div>
        </div>

        {/* Narrative continuation */}
        <div className="space-y-6 text-base md:text-lg leading-relaxed">
          <p>
            The first test train from Ipswich ran to a temporary station at Bury St Edmunds on 26 November 1846, a fortnight before the official opening. Greenwich Mean Time was later adopted so railway timetables could be standardised. Unemployment and poverty, followed by disease and unrest, continued to increase. Emigration posters and advertisements were aimed at the working classes. Thousands decided to leave for Canada, Australia, America, South Africa and other African nations. The New Zealand link was through the Suffolk parish of Reverend Charles Torlesse and his brother-in-law, Edward Gibbon Wakefield, the controversial mastermind behind large-scale British settlements in South Australia, Canada and New Zealand. He convinced many, including John Denton, to support the NZ Society and the Anglican Canterbury Association (1843), seven years before it would send its First Four Ships to Lyttelton.
          </p>
        </div>

        {/* Image 7A: Bridge Farm */}
        <ArchivalImageCard
          src="/images/7A Bridge Farm.jpg"
          alt="Bridge Farm"
          caption="Bridge Farm"
          filenameHint="7A Bridge Farm.jpg"
        />

        {/* Letter 3: John Denton to Thomas (8 August 1849) */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              John Denton to his brother Thomas, of Bridge Farm on the Rushbrooke Estate.
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              8 August 1849
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>John Denton to Thomas & Sophia</span>
              <span>8 August 1849</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dear Tom and Sophia,<br />
                I hope this finds you as well as we all are here, but there is unrest and apprehension at every turn. There’s been much talk in the parish about emigration, and our beloved Rev. speaks often of its benefits. Families are separated, but they are by death, the poorhouse or transportation which is the worst evil. We have discussed the topic from many angles and I’m still working through my own thoughts. When I look at the diminishing likelihood of being able to put each of my boys on to a decent farm, I wonder if we should be looking at these new lands, but I’m over the age limit. You and I were fortunate indeed that we stepped into our farms, thanks to our father and his father before him. Anne is not happy with these discussions. Bless her, she is so happy here with domestic matters.
              </p>

              <p>
                There are arson attacks in town every week, and even in local barns. A group of ragged Irish came here last week and I was glad to offer them shelter and food, and some wages for a few days’ work. They said most of their families were dead or off to America.
              </p>

              <p>
                I must not be boastful, but I’m pleased to share Arthur’s success with his horses. He’s a beautiful rider and can do anything with horses. It’s a gift indeed. William is already very systematic and he loves book keeping.
              </p>

              <p>
                Anyway, I am sure you have thought long and hard about these things too and I look forward to discussing them with you.<br />
                Love from John and Anne"
              </p>
            </div>
          </div>
        </div>

        {/* Letter 4: Anne's letter to Sophia (7 Aug 1850) */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Anne’s letter to Sophia
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              Fishley Farm, 7 Aug 1850
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>7 August 1850</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dear Sister I write with some apprehension, dread even. John talks every night of this Canterbury Association. Fortunately, he’s too old as they only want men under 40, but the boys might be sent out and I would hate that. The first ships are leaving in December.
              </p>

              <p>
                We got a fright when there was another local outbreak of deadly cholera. I didn’t go near Bury just to be safe. Many people have been getting drunk and put up before the magistrates. The Vicar said drinking was often a last act of desperation – women too, can you believe? I heard there are 200 poor souls in the workhouse. It wasn’t built for such a number. I can’t see any good ending this year.
              </p>

              <p>
                John has tried to give the Irish the dignity of a day’s wage for a day’s work, and food and shelter for one or two nights. He was criticised because there’s so much need on our back door, but John believes the Irish have it worse. They are saying there will be a million dead from the famine. I wish John was more like your Bartrum - he isn’t talking about fleeing. He just keeps going and does what he can. I know of his generosity.
              </p>

              <p>
                John is in love with these ideals, but I’m not bewitched. Sometimes I feel I am being judged by the parish for not being enthusiastic about this utopian society they talk and dream about. There is growing resentment at the punishment people are getting for minor offences, even for stealing bread when they are starving. I have heard of young girls being sent out to the penal settlements. One fears for what will happen to them out there when they have served their harsh sentences.
              </p>

              <p>
                Georgiana is my delight. Little Alice is becoming quite an impudent little girl who has John completely at her mercy.<br />
                Much love from Anne"
              </p>
            </div>
          </div>
        </div>

        {/* Image 7B: Fishley Farm 1850 */}
        <ArchivalImageCard
          src="/images/7B Fishley Farm 1850.jpg"
          alt="Fishley Farm 1850"
          caption="Fishley Farm 1850"
          filenameHint="7B Fishley Farm 1850.jpg"
        />

        {/* Letter 5: Anne's letter to Sophia (17 August 1850) */}
        <div className="space-y-4">
          <p className="font-serif font-bold text-[#4a3525] text-lg">
            Fishley Farm, 17 August 1850
          </p>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>17 August 1850</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "My dearest Sophia<br />
                Sit down. I am so distressed. John wants us to emigrate to New Zealand. The age limit has gone because they couldn’t get enough people to fill their damned boats. He says he is thinking of the boys. I went down on my knees beseeching God to stop this, but John is determined and the boys are enthusiastic too.
              </p>

              <p>
                The planned departure date is 8 Sept. I have to pack for a completely new life in a place I know nothing about. John is getting everything ready for farming - he’s got the boys painting equipment in red and blue.
              </p>

              <p>
                I am in such confusion. Can you come and help me? I’ve got the servants equally confused! I’m using a guide to emigrants and putting things out to pack, but I will have to rely on you to dispose of what we leave behind.
              </p>

              <p>
                We won’t be going to a proper house, but have to live in tents for a start – can you imagine? John will buy land immediately on arrival. We will be in a first-class fore cabin so we take our own furniture. I have insisted we take Alice’s doll house.
              </p>

              <p>
                John has arranged for Fred and Edward to go as steerage passengers, at 15 pounds each, but they cannot take so much with them. Arthur and John are old enough to be listed as agricultural labourers so they get assisted passages from the Canterbury Association Land Fund. They all talk excitedly about it at dinner while I sit there like an upright wet blanket! I do not share this wild excitement. I hated it when Mrs Lancaster called in and asked if she could take on our rat catcher. She said she knew a woman who wanted our domestics, as though we were giving away cases of spare summer fruit.
              </p>

              <p>
                I have remonstrated with John in private, but he seems ten years younger in his enthusiasm. Our ship is the Sir George Seymour, the largest of the four, under a Commander Thomas Goodson. Oxford University has given 2000 books to each ship so at least we’ll have something to read. I will take my dark pink costume to wear when we arrive. Georgiana chose a blue arrival outfit and I have a pretty pink smock for Alice.
              </p>

              <p>
                My greatest fears, apart from the journey itself, are that we will never return to my beloved Suffolk, or see you or our parents again. John said we should be able to make a return journey within five years which seems an eternity away.
              </p>

              <p>
                I have prayed for guidance, and talked with our Rev. as I must come to peace with this. John believes this will give our boys each a chance for their own farms but will there be suitable men for my daughters? I will send Georgiana back to you when she is older so she can experience English society.
              </p>

              <p>
                John said our four ships will have more than 750 passengers so there should be decent company when we finally get there.
              </p>

              <p>
                Please come and see me as soon as you can. I will visit our parents before we sail.<br />
                Love from your soon-to-be-departing, but despairing sister.<br />
                Anne xx"
              </p>
            </div>
          </div>
        </div>

        {/* Image 8: Sir George Seymour */}
        <ArchivalImageCard
          src="/images/8 Sir George Seymour.jpg"
          alt="Sir George Seymour"
          caption="The Sir George Seymour"
          filenameHint="8 Sir George Seymour.jpg"
        />

        {/* Narrative: Voyage of Sir George Seymour */}
        <div className="space-y-6 text-base md:text-lg leading-relaxed">
          <p>
            The Sir George Seymour weighed anchor at Plymouth at 10 am on Sunday 8 September 1850. Even though there was a cloudless sky and a fine breeze from the east most of the 227 passengers came down with varying levels of seasickness over the first four days. Then ladies sewed and chatted on the deck while gentlemen consulted nautical charts, played chess, read and had a great deal of conversation. There were morning and evening services, Holy Communion and a daily Psalm reading. They mourned the deaths of several infants and their burials at sea. The first sighting of the new land was of Stewart Island on 11 December.
          </p>
        </div>

        {/* Letter 6: 16 December 1851 – summer! */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Within sight of New Zealand, nearly there!
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              16 December 1851 – summer!
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>16 December 1851</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dearest Sophia<br />
                I write this knowing we can soon be able to post letters home as we sail north towards our destination. We have been mostly on the deck looking at mountain peaks that seem to form a backbone to the land. Everyone is very excited.
              </p>

              <p>
                The worst moments were when Fred was so ill the doctor thought he would not survive, but he’s thrived in recent days. Arthur was the hero of the moment when he raised the alarm after discovering a fire in the hold after some silly chap dropped his lantern into straw. The men and boys worked hard getting water on to the fire and we were all thankful when they quenched the flames.
              </p>

              <p>
                There was meant to be a school on the ship, but it never happened. I don’t think it mattered as the children learned so much on the voyage. There was one scandal. The original surgeon was so hopeless he was sacked, but that gave him plenty of time to mix with steerage passengers and he was soon in a very intimate friendship with a rather graceless woman. They had no inhibitions showing off their shameful passion. I’m sure there will be repercussions when we dock.
              </p>

              <p>
                John enjoyed the voyage, talking every day with other colonists about how they will develop their farms. He retires quite exhausted from all the talking and listening! I made some friends, but more acquaintances. Much of my time was taken up with the children. I gave them short daily lessons. Georgiana really blossomed and made some close friends in the way of a happy 12-year-old. The other girls are well mannered and nicely behaved. Little Alice is my delight, really and truly, and every day she charms me anew. Toddler Thomas has won many admirers with his amusing ways and great curiosity. I try to stop people calling him Tommy or Tom, but I’m not winning!
              </p>

              <p>
                Arthur and William are like little men, absorbing all the talk about them. They are impatient to dock and get going. Fred and Edward aren’t far behind them but there is still something more boyish about them.
              </p>

              <p>
                You know how apprehensive I was to leave Suffolk. I admit I’m feeling excited to be nearly there, but still with a hint of terror. I unpacked my arrival outfit and washed the girls’ hair to give them nice elaborate plaits for the town. I’ve missed the pleasure of shopping and promenading.
              </p>

              <p>
                I hope the other ships arrive soon so we will have a great crowd. We may remain on the ship until everything is unloaded. I’m not sure I fully understand what it all that means but they say the settlement has been planned for a long time.
              </p>

              <p>
                I will write more once we have arrived.<br />
                Love from Anne"
              </p>
            </div>
          </div>
        </div>

        {/* Letter 7: Lyttelton, 18 Dec ‘50 */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Lyttelton, still on board
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              18 Dec ‘50
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>18 December 1850</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Oh Sophie,<br />
                I’ve been in tears since we arrived 10 am yesterday after 99 days. The Charlotte Jane and Randolph were already here. The Governor, Sir George Grey and Lady Grey were on their boat and made gracious welcomes on a very sunny and hot day BUT there are no shops, no servants or nurses. There are plain wooden barracks, rough roads, and some small structures (I would hardly call them houses) on a steep hillside. There are no paths fit for a woman in a fine gown.
              </p>

              <p>
                We prepared to go ashore, but were soon in tears when we saw the crude savage reality. The list of things we were given to pack was ridiculous. I can’t see where John would wear a morning coat let alone where I would wear any of my best gowns. Georgiana tried to comfort me, but she and her friends quickly raced ahead to explore. I had imagined a new and small version of Plymouth. John took it in his stride and said the future is over the hills where there is a large plain. The boys explored all day and saw it as an adventure.
              </p>

              <p>
                Promise to send me constant letters with all the news of home. I am going to need light cottons and linen so can you please send some lengths out for lighter clothes for the children. I will send this off now but will write again when I know where we will be living.
              </p>

              <p>
                WHAT HAVE WE DONE? I cannot share John’s enthusiasm. I have dread in my heart, not delight.
              </p>

              <p>
                Write to me with all the news of home. Love to everyone, Anne."
              </p>
            </div>
          </div>
        </div>

        {/* Letter 8: Still on board, 26 December 1850 */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Still on board
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              26 December 1850
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>26 December 1850</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dear Sophie<br />
                You would have been having a lovely dinner in the dining room yesterday while we celebrated Christmas with a service and then dinner on board as things are not yet ready for us in Christchurch. Our farm equipment is still in the hold. The ship’s cook made a surprisingly marvellous meal. It’s quite different having Christmas under a hot and very bright summer sun. We had fish instead of turkey. I quite enjoyed it and felt it might be one of the last “formal” events for some time.
              </p>

              <p>
                I have heard of the crowded tent and hut settlement we will be going to for a few months until John’s land is sorted. He has to buy a town block as well as the rural land.
              </p>

              <p>
                The other women colonists share most of my feelings. We are all apprehensive about how we will manage without any help and many missed out on getting a servant from steerage. I’m hopeful I will find a domestic when we are settled.
              </p>

              <p>
                The children got sun burned in the first days. They run about much of the day onshore, and have climbed up the steep path they call the Bridle Path over the hills. They said they can see for miles across the plains to mountains in the distance. I have not been up there yet as I’ve been mending and washing clothes. Please don’t laugh!
              </p>

              <p>
                John has settled in with a group of others who are also buying farmland. He is confident they will all work together. I hope he is not disappointed although they all seem to be God-fearing and (overly) idealistic. I wish that I could share it, but perhaps it is my nature to hold back.
              </p>

              <p>
                The steerage passengers have all gone from the ship.
              </p>

              <p>
                I thought of you all at home on Christmas Day, although you will be celebrating as I write this. I can imagine your little ones on Father’s knee and Mother making such a fuss of them as she always does. We didn’t have any exchange of presents. I had not thought to pack anything to give the children yesterday. They went swimming in the afternoon – can you imagine that?
              </p>

              <p>
                XXXXXXXX Anne"
              </p>
            </div>
          </div>
        </div>

        {/* Letter 9: Christchurch, 10 May 1851 */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Christchurch
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              10 May 1851
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>10 May 1851</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dear Sophia,<br />
                My beautiful Georgiana died two days ago, from a dreadful fever that has swept through these horrible, cramped, unhygienic camping conditions. We have been living in a basic hut, truly horrible, so crude. I cannot believe we have survived so long here. I feared the worst when so many people had died, and then Georgiana developed a high fever and there was nothing I could do to help, but hold her as she slipped away. I knew there was no hope for two dreadful days.
              </p>

              <p>
                This was so much worse than losing little Elizabeth. My darling Georgiana, with all her lovely ways and joyous company. I can only write briefly as I am still stunned. I am even more fearful that this dreadful fever will kill more in this Hagley Park. To think of coming all this way and to bury my daughter five months later.
              </p>

              <p>
                John has hardly spoken. I am sure he feels a sense of responsibility for the way we are living. I have written to Mother and Father. I hope you can see them when you get this to be some comfort to them. I feel such a sense of guilt even though the fever is so widespread. Pray for me and all of us.<br />
                Yours, heartbroken and in despair, Anne."
              </p>
            </div>
          </div>
        </div>

        {/* Image 7C: Settlers' huts near Pilgrims Well */}
        <ArchivalImageCard
          src="/images/7C Settlers_ huts near Pilgrims Well, Hagley Park.jpg"
          alt="Settlers’ huts near Pilgrims Well, Hagley Park"
          caption="Settlers’ huts near Pilgrims Well, Hagley Park"
          filenameHint="7C Settlers_ huts near Pilgrims Well, Hagley Park.jpg"
        />

        {/* Letter 10: Cathedral Square, Christchurch, 25 June 1851 */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Cathedral Square, Christchurch
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              25 June 1851
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>25 June 1851</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dear Sophia,<br />
                Our lovely William died three days ago of the same fever that killed Georgiana. Dr Gundry is handing out costly medicines, but it’s not stopping the deaths. My heart is still breaking from losing my beautiful Georgiana. We shifted away from that death park, but the fever is following people all over the settlement. William was only 16 and now he lies next to his sister. The dear boy was so excited about what he might do here, on the land called Opawa that John has taken up, by a muddy river. It is so primitive you would not believe it. There are river crabs in the mud and people are living under dank tents. I refuse to stay there. John is ageing before my eyes with this grief and I pray he stays well. I have no idea what I would do if he went too. We are a very sad household. John’s evening prayers give me very little comfort. I yearn for the love you would give me if you were here. I can hardly write, so forgive this brevity. I cannot write separately to our parents and I rely on you to break this sad news to them. I know how much you loved Georgiana, and I think of Juliette who was so close to my darling girl. How can we bear these things, I am not sure.<br />
                Yours, heartbroken again and still in despair, Anne."
              </p>
            </div>
          </div>
        </div>

        {/* Letter 11: Cathedral Square, Christchurch, 28 July 1851 */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Cathedral Square, Christchurch
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              28 July 1851
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Anne Denton to Sophia</span>
              <span>28 July 1851</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Oh, my dearest Sophia,<br />
                Today we buried little Edward, only 11 years old, and now forever in the ground next to Georgiana and William, with the three crosses in the ground our dreadful reward for coming to this awful country. Dr Gundry’s expensive medicines are not stopping people dying and it’s getting worse in the cold winter. I thought I had grief enough but this has become almost unbearable. I prayed and prayed but still I lost my Edward. John is grieving in his way too, and I fear for his health - what on earth would I do then?
              </p>

              <p>
                The number of innocent children the colonists have buried is simply shocking. Arthur, Fred and little Thomas are all silent and withdrawn. Even little Alice understands. How can life be so cruel?
              </p>

              <p>
                But sister dear, the secret I share only with you is that I fear that I am developing the first symptoms of this fever and that is an even deeper grief I carry within me, because if it claims me who will look after my babies? I am grieving, fearful, wildly fearful, wanting to do my best, wondering how I can stop this thing … less than a year ago that I was back in my beautiful Suffolk, and now, after what has happened in these short months, I’m totally bewildered as to why John thought it would be better here. I hate this place. It has robbed me of three beautiful children, and worse to come I dread.
              </p>

              <p>
                John is bending under the weight of his grief and sense of responsibility and he has aged dreadfully. I am trying to hide my condition from him but for how long? I pray I am mistaken.
              </p>

              <p>
                Sophia, you know me better than anyone in this world. Do what you can for my little family. Pray for me. Remember me. Know that I have loved you forever. Tell my parents – I’m not sure what you can tell them. The anticipation of their grief makes me feel even more devastated, if that is possible.
              </p>

              <p>
                Yours, forever your sister who has treasured you all her life, with love, Anne xx."
              </p>
            </div>
          </div>
        </div>

        {/* Letter 12: Cathedral Square, Christchurch, 16 August 1851 */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Cathedral Square, Christchurch
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              16 August 1851
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>John Frederick Denton to Sophia & Parents</span>
              <span>16 August 1851</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dear Sister-in-Law Sophia,<br />
                I regret I have the saddest duty to tell you and your parents that our beloved Anne Maria died of fever on 13 August. We came as a family of nine and now we are five. I promised Anne I would keep our family together, and write to you to keep you in our lives.
              </p>

              <p>
                It has been a lot harder here than we anticipated. I still have a strong belief that, in time, there will be worthwhile rewards for future generations - that is how I console myself.
              </p>

              <p>
                My land is ready for cultivation. Arthur and Fred are strong young men who do a good day’s work. Little Alice is being looked after by kind ladies here. Thomas is a sturdy wee boy. Anne’s loss weighs heavy on us all. I mourn my beloved wife who dutifully came here with me. I will carry that responsibility for the rest of my life.
              </p>

              <p>
                I anticipate you will ask how you can help. The two little ones need clothes, shoes, boots and Manchester, as they are growing so quickly. When they outgrow clothes, we quickly find another small child to give them to. The boys would appreciate journals about the new agricultural machines. Sturdy boots, canvas and warm clothes are always welcome.
              </p>

              <p>
                We will not be able to return to England. We have high medical costs to repay and our aim is to get established at Opawa and build a house. I pray this is the end of our tragedies.
              </p>

              <p>
                I deeply loved Anne. She found great contentment with her children. She was already popular amongst the women here. I have lost my beloved companion and the children their adored mother. You have lost a most treasured sister and I feel for your parents who will never see their loved daughter again.
              </p>

              <p>
                With deepest regret at having to write this letter,<br />
                Your brother-in-law, John."
              </p>
            </div>
          </div>
        </div>

        {/* Letter 13: Opawa, Christchurch, 28 September 1856 */}
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-serif font-bold text-[#4a3525] text-lg">
              Opawa, Christchurch
            </p>
            <p className="text-sm font-serif text-[#7a5b3a] font-bold">
              28 September 1856
            </p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] border-y border-r border-[#d8ceb9] shadow-xs space-y-4 font-serif text-[#2c221e]">
            <div className="flex items-center justify-between border-b border-[#d8ceb9] pb-2 text-xs uppercase tracking-wider font-bold text-[#7a5b3a]">
              <span>Arthur Denton to Aunt Sophia</span>
              <span>28 September 1856</span>
            </div>

            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                "Dear Aunt Sophia,<br />
                It is with a heavy hand that I sit down to write to you and our grandparents with the sad news that Father passed away on 25 September. He never really got over Mother’s death, so soon after Georgie, Will and Edward.
              </p>

              <p>
                Fred and I will carry on with the farm. Alice has been living for some time with the Bridge family. To be honest, she is rather spoiled there and getting many advantages. Mother would be pleased to see what a fine little lady she is becoming! Tommy is with a foster family. He’s an independent wee chap and already a champion rider. I doubt he has any real memory of our mother, and he only knew Father when he was unwell.
              </p>

              <p>
                I remember those happy days at Fishley Farm and all my friends there. We have a new circle of friends here. Most are in similar circumstances and all hopeful. Father’s foresight in bringing out all our farm machinery allowed me to do lucrative contract work, which has paid for fencing and development. We plan to sell and go further out onto the plains to get larger independent farms.
              </p>

              <p>
                Take some comfort that your nephews and niece are starting to get ahead. I look back on Fishley Farm as a gentle easy life, but this country has now got into our hearts. Fred and I are confident we will end up being proper farmers - although we won’t be gentlemen farmers. We have a little sod cottage which is quite comfortable but a great contrast to our lovely brick house at Fishley as you could possibly imagine.
              </p>

              <p>
                I’ve been keeping company with Julia Wilson and hope to be in a position to ask her to marry me next year. She came out in January 1851 with her parents who have a popular bakery so Julia is a great cook! It made a big difference to my life when I got to know her.
              </p>

              <p>
                Fred has been working with bullocks as there aren’t many horses here. When we aren’t working together on the farm, he has a carting business and is often away for some time and it’s given him the chance to have a good look at other areas. He’s attracted to an area called Eyreton, north of Christchurch. He has been walking out with Esther King, who is about ten years older than him. They get on very well as he’s quite a serious chap and has always seemed older than his age. There is a shortage of young women in the colony.
              </p>

              <p>
                When we each have our own first farm, we will have repaid our parents for their bravery and sacrifice. We talk about them often. 1851 was almost unbearable. Now five years on we can talk of them with fond memories, but the loss of them and Georgie, Will and Edward will never go away.
              </p>

              <p>
                It is a sad fact of this colonial life that many people die from illness, accidents and too many drown in the wild and dangerous rivers that are very different to our streams, creeks, brooks and gentle rivers of home. In the towns too many young men just become drunkards. A lot of young men come out here and think it’s going to be easy, but after a short time they return home as sorry drunken messes.
              </p>

              <p>
                We were fortunate to have the benefit of land, machinery and inherited capital. Fred and I will do as much planting as we can when we get our own farms. Pine trees look like they could be harvested within 20 years.
              </p>

              <p>
                Please keep writing. We have greatly appreciated your many parcels of clothes, shoes and boots and always learned a lot from the journals you sent. We make notes, then pass them on to other chaps. One day I hope you can visit us here and be proud of Anne’s sons.
              </p>

              <p>
                With love from your nephew,<br />
                Arthur"
              </p>
            </div>
          </div>
        </div>

        {/* Sub-page Navigation footer */}
        {onNavigate && (
          <div className="border-t border-[#d8ceb9] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <button
              onClick={() => onNavigate('journey')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#f3ede0] hover:bg-[#e8dec9] text-[#2c221e] font-serif text-sm font-bold border border-[#d8ceb9] transition"
            >
              <ArrowLeft className="w-4 h-4 text-[#7a5b3a]" />
              <span>The Journey Overview</span>
            </button>
            <button
              onClick={() => onNavigate('journey-essentials')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#4a3525] hover:bg-[#634833] text-amber-100 font-serif text-sm font-bold transition shadow-sm border border-amber-200/20"
            >
              <span>Next: Essentials for a new land</span>
              <ArrowRight className="w-4 h-4 text-amber-300" />
            </button>
          </div>
        )}

      </article>

    </div>
  );
};
